import { ErrorMessage, Field, Formik } from 'formik';
import { observer, useLocalStore } from 'mobx-react-lite';
import React, { useContext } from 'react';
import * as yup from 'yup';
import { SaveChangesContext } from '../../contexts/SaveChanges';
import { UsersContext } from '../../contexts/Users';
import { COMPLETION_STATUS } from '../../models/Completion';
import { formatDate } from '../../utils/format-date';
import * as CSS from './elements/Completion';
import SummativeComment from './SummativeComment';

const completionSchema = yup.object().shape({
  summary: yup.string().trim().required('Please enter a summative comment'),
});

const CompletionPanel = observer(({ student, itemId, collection }) => {
  const usersStore = useContext(UsersContext);
  const saveChangesStore = useContext(SaveChangesContext);
  const store = useLocalStore(() => ({
    isCompleting: false,
    setIsCompleting(isCompleting) {
      store.isCompleting = isCompleting;
    },
  }));

  const staffId = usersStore.loggedInUserId;
  const completion = student.private.getItemCompletion(itemId);
  const isComplete = student.private.isItemComplete(itemId);
  const previousSignOffs = (completion?.history || [])
    .filter(
      ({ status, changedAt }) =>
        status === COMPLETION_STATUS.complete &&
        (!isComplete || changedAt < completion.changedAt)
    )
    .reverse();

  const handleSubmit = async ({ summary }) => {
    await student.private.markItemComplete(
      itemId,
      collection,
      summary.trim(),
      staffId
    );
    store.setIsCompleting(false);
  };

  const handleReopen = () => {
    saveChangesStore.setText({
      heading: 'Reopen',
      message:
        'Are you sure you want to reopen this? The summative comment will be kept in its history.',
      leavingText: 'Yes',
      remainingText: 'No',
    });
    saveChangesStore.open(() =>
      student.private.reopenItem(itemId, collection, staffId)
    );
  };

  return (
    <CSS.CompletionContainer data-testid={`CompletionPanel-${itemId}`}>
      {isComplete ? (
        <>
          <SummativeComment
            summary={completion.summary}
            heading="Summative comment"
          />
          <CSS.DeleteButton onClick={handleReopen}>Reopen</CSS.DeleteButton>
        </>
      ) : store.isCompleting ? (
        <Formik
          initialValues={{ summary: '' }}
          validationSchema={completionSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <CSS.CompletionForm>
              <CSS.CompletionLabel htmlFor={`summary-${itemId}`}>
                Summative comment
              </CSS.CompletionLabel>
              <CSS.CompletionTextAreaContainer>
                <Field
                  id={`summary-${itemId}`}
                  name="summary"
                  component={CSS.TextArea}
                  rowsMin={3}
                  placeholder="Summarise the student's work on this"
                />
              </CSS.CompletionTextAreaContainer>
              <ErrorMessage name="summary" component={CSS.CompletionError} />
              <CSS.ButtonRow>
                <CSS.CreateButton type="submit" disabled={isSubmitting}>
                  Mark as complete
                </CSS.CreateButton>
                <CSS.Button
                  type="button"
                  $variant="secondary"
                  onClick={() => store.setIsCompleting(false)}
                >
                  Cancel
                </CSS.Button>
              </CSS.ButtonRow>
            </CSS.CompletionForm>
          )}
        </Formik>
      ) : (
        <>
          <CSS.DetailSectionHint>
            When the work meets the {collection === 'tasks' ? 'task' : 'target'}
            , mark it complete with a summary for the student.
          </CSS.DetailSectionHint>
          <CSS.CreateButton onClick={() => store.setIsCompleting(true)}>
            Mark as complete
          </CSS.CreateButton>
        </>
      )}
      {previousSignOffs.length > 0 && (
        <>
          <CSS.HistoryHeading>Previous summative comments</CSS.HistoryHeading>
          <CSS.HistoryList>
            {previousSignOffs.map(({ changedAt, summary }) => (
              <li key={changedAt.getTime()}>
                {formatDate(changedAt)}: {summary?.text}
              </li>
            ))}
          </CSS.HistoryList>
        </>
      )}
    </CSS.CompletionContainer>
  );
});

export default CompletionPanel;
